import configs from "../configs.js";
import { getAuthHeaders } from "../ui/utils.js";

/**
 * Pure API client for Dashboard API that automatically handles workspace_id and authentication tokens.
 * This client should be configured with a workspaceIdGetter function from the AuthContext.
 */
export default class DashboardApiClient {
  constructor() {
    this.workspaceIdGetter = null;
    this.baseUrl = configs.CUSTOM_API_URL;
    this.reauthHandler = null;
    this.getAuthHeaders = getAuthHeaders;
  }

  setReauthHandler(handler) {
    this.reauthHandler = typeof handler === "function" ? handler : null;
  }

  isReauthCanaryEnabled() {
    // Default-on rollout: only disable explicitly with auth_canary_dashboard_reauth=0
    return localStorage.getItem("auth_canary_dashboard_reauth") !== "0";
  }

  /**
   * Set a function to get the current workspace ID from context.
   * This should be called from components that have access to AuthContext.
   * @param {Function} getter - Function that returns the current workspace ID
   */
  setWorkspaceIdGetter(getter) {
    this.workspaceIdGetter = getter;
  }

  /**
   * Get the current workspace ID from context or localStorage.
   * @returns {string} The workspace ID
   * @throws {Error} If no workspace is selected
   */
  getWorkspaceId() {
    // Try to get workspace_id from context first (via injected getter)
    if (this.workspaceIdGetter) {
      const workspaceId = this.workspaceIdGetter();
      if (workspaceId) {
        return workspaceId;
      }
    }

    // Fallback to localStorage (for backward compatibility during transition)
    const workspaceId = localStorage.getItem("selected_workspace_id");
    if (!workspaceId) {
      throw new Error("No workspace selected. Please select a workspace first.");
    }
    return workspaceId;
  }

  async getRequestAuthHeaders(url, method) {
    return await this.getAuthHeaders(url, method);
  }

  /**
   * Build a URL with workspace prefix for workspace-scoped resources.
   * @param {string} path - The resource path (e.g., '/projects', '/projects/123')
   * @returns {string} The full URL with workspace prefix
   */
  buildWorkspaceUrl(path) {
    const workspaceId = this.getWorkspaceId();
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${this.baseUrl}/workspaces/${workspaceId}/${cleanPath}`;
  }

  /**
   * Build a URL for workspace-level resources (no nested resource).
   * @param {string} path - The resource path (e.g., '/workspaces')
   * @returns {string} The full URL
   */
  buildUrl(path) {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${this.baseUrl}/${cleanPath}`;
  }

  /**
   * Make an authenticated API request with automatic workspace handling.
   * @param {string} url - The full URL or path (if path, will be prefixed with workspace)
   * @param {Object} options - Fetch options (method, body, headers, etc.)
   * @param {boolean} useWorkspace - Whether to prefix the URL with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async request(url, options = {}, useWorkspace = true) {
    // Build the full URL
    const fullUrl = useWorkspace ? this.buildWorkspaceUrl(url) : this.buildUrl(url);

    const execute = async () => {
      const method = options.method || "GET";
      const authHeaders = await this.getRequestAuthHeaders(fullUrl, method);

      // Merge headers
      const headers = {
        "Content-Type": "application/json",
        ...authHeaders,
        Accept: "application/json",
        ...options.headers
      };

      // Remove headers explicitly set to null or undefined
      Object.keys(headers).forEach(key => {
        if (headers[key] === undefined || headers[key] === null) {
          delete headers[key];
        }
      });

      return fetch(fullUrl, {
        ...options,
        headers
      });
    };

    let response = await execute();

    const shouldRetryWithReauth =
      response.status === 401 && this.isReauthCanaryEnabled() && typeof this.reauthHandler === "function";

    if (!shouldRetryWithReauth) {
      return response;
    }

    const didReauthenticate = await this.reauthHandler({ path: url, useWorkspace }).catch(() => false);

    if (!didReauthenticate) {
      return response;
    }

    response = await execute();
    return response;
  }

  /**
   * GET request helper.
   * @param {string} path - The resource path
   * @param {Object} queryParams - Optional query parameters
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async get(path, queryParams = {}, useWorkspace = true, signal = null) {
    const params = new URLSearchParams(queryParams);
    const url = params.toString() ? `${path}?${params.toString()}` : path;
    return this.request(
      url,
      {
        method: "GET",
        signal
      },
      useWorkspace
    );
  }

  /**
   * POST request helper.
   * @param {string} path - The resource path
   * @param {Object} body - The request body (will be JSON stringified)
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async post(path, body = {}, useWorkspace = true, requestOptions = {}) {
    return this.request(
      path,
      {
        ...requestOptions,
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          ...(requestOptions.headers || {})
        }
      },
      useWorkspace
    );
  }

  /**
   * PUT request helper.
   * @param {string} path - The resource path
   * @param {Object} body - The request body (will be JSON stringified)
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async put(path, body = {}, useWorkspace = true) {
    return this.request(
      path,
      {
        method: "PUT",
        body: JSON.stringify(body)
      },
      useWorkspace
    );
  }

  /**
   * PATCH request helper.
   * @param {string} path - The resource path
   * @param {Object} body - The request body (will be JSON stringified)
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async patch(path, body = {}, useWorkspace = true) {
    return this.request(
      path,
      {
        method: "PATCH",
        body: JSON.stringify(body)
      },
      useWorkspace
    );
  }

  /**
   * DELETE request helper.
   * @param {string} path - The resource path
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Response>} The fetch response
   */
  async delete(path, useWorkspace = true) {
    return this.request(path, { method: "DELETE" }, useWorkspace);
  }

  /**
   * Handle response and parse JSON with error handling.
   * @param {Response} response - The fetch response
   * @returns {Promise<Object>} The parsed JSON data
   * @throws {Error} If the response is not ok
   */
  async handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`API request failed: ${response.status} ${errorText}`);
      error.status = response.status;
      try {
        const json = JSON.parse(errorText);
        error.error = json.error;
        error.code = json.code || json.error;
        error.message = json.message || error.message;
      } catch (e) {
        // ignore json parse error
      }
      throw error;
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }

    return await response.text();
  }

  /**
   * Convenience method: GET request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} queryParams - Optional query parameters
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async getJson(path, queryParams = {}, useWorkspace = true, signal = null) {
    const response = await this.get(path, queryParams, useWorkspace, signal);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: POST request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} body - The request body
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async postJson(path, body = {}, useWorkspace = true, requestOptions = {}) {
    const response = await this.post(path, body, useWorkspace, requestOptions);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: PUT request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} body - The request body
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async putJson(path, body = {}, useWorkspace = true) {
    const response = await this.put(path, body, useWorkspace);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: PATCH request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {Object} body - The request body
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async patchJson(path, body = {}, useWorkspace = true) {
    const response = await this.patch(path, body, useWorkspace);
    return this.handleResponse(response);
  }

  /**
   * Convenience method: DELETE request with automatic JSON parsing.
   * @param {string} path - The resource path
   * @param {boolean} useWorkspace - Whether to prefix with workspace (default: true)
   * @returns {Promise<Object>} The parsed JSON response
   */
  async deleteJson(path, useWorkspace = true) {
    const response = await this.delete(path, useWorkspace);
    return this.handleResponse(response);
  }
}

// Export a singleton instance
export const dashboardApiClient = new DashboardApiClient();
